/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {navigationHeaderHeight} from '../base/drawer'

const ICON_SIZE = 4

const mainContainer = {
    // needed to center logo in header
    flexBasis: 0,
    flexGrow: 1
}

const icon = {
    boxSize: ICON_SIZE
}

const iconButton = {
    lineHeight: '1',
    height: ICON_SIZE,
    width: ICON_SIZE,
    minWidth: ICON_SIZE
}

// keep in mind the style is used on regular header and also on checkout-header
export default {
    baseStyle: {
        container: {
            display: 'flex',
            flexDirection: 'column',
            transition: '.5s',
            height: navigationHeaderHeight.default
        },
        headerContainer: {
            display: 'flex',
            flexDirection: 'column'
        },
        mainContainer: {
            minWidth: 'xs',
            maxWidth: 'container.xxl',
            width: 'full',
            mx: 'auto',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingX: {
                base: 4,
                lg: 6,
                xl: 8
            },
            paddingBottom: 2,
            paddingTop: {base: 2, lg: 0},
            gap: 8
        },
        leftContent: {
            ...mainContainer,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 2,
            order: {base: -1, lg: 0},
            minWidth: 0,
            flexGrow: 1
        },
        rightContent: {
            ...mainContainer,
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 6,
            alignSelf: 'stretch',
            flexGrow: {base: 1, lg: 0}
        },
        bodyContainer: {
            flex: '1'
        },
        logo: {
            boxSize: {base: 14, xl: 16}
        },
        icon,
        iconButton,
        accountButton: {
            ...iconButton
        },
        menuButton: {
            ...iconButton,
            display: {lg: 'none'}
        },
        brandButton: {
            height: 'min'
        },
        searchButton: {
            ...iconButton
        },
        cartButton: {
            ...iconButton,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center'
        },
        cartIconContainer: {
            position: 'relative'
        },
        cartBadge: {
            variant: 'notification'
        },
        signout: {
            width: '100%',
            borderRadius: '4px',
            height: 11,
            padding: 4,
            py: 3,
            marginTop: 1,
            _hover: {
                background: 'gray.50'
            }
        },
        signoutText: {
            fontSize: 'sm',
            fontWeight: 'normal'
        },
        signoutIcon: {
            marginRight: 2
        },
        helperContainer: {
            display: {base: 'none', lg: 'flex'},
            maxWidth: 'container.xxl',
            width: 'full',
            mx: 'auto',
            paddingX: {lg: 6, xl: 8},
            paddingTop: 2,
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 5,
            alignSelf: 'stretch'
        }
    },
    variants: {
        checkout: {
            mainContainer: {
                // reset base styles
                paddingX: null,
                paddingY: null,
                // custom styles
                maxWidth: 'container.xxl',
                marginLeft: 'auto',
                marginRight: 'auto',
                height: {
                    base: '4.375rem',
                    lg: '6.875rem'
                },
                px: {
                    base: 4,
                    lg: 6,
                    xl: 8
                },
                gap: 13.5
            }
        },
        messageBar: {
            container: {
                height: navigationHeaderHeight.messageBar
            }
        }
    },
    parts: [
        'container',
        'leftContent',
        'rightContent',
        'bodyContainer',
        'logo',
        'icon',
        'iconButton',
        'accountButton',
        'brandButton',
        'cartButton',
        'cartIconContainer',
        'cartIcon',
        'cartBadge',
        'signout',
        'signoutText',
        'signoutIcon',
        'mainContainer',
        'helperContainer',
        'searchButton',
        'menuButton'
    ]
}
